import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import {
  Checkbox,
  Typography,
  MenuItem,
  Button,
  MenuList,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  TablePagination,
  CircularProgress,
  Tooltip,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SearchInput from 'components/SearchInput';
import { useDebounce } from 'helpers';
import { updatePaginatedFilters } from 'store/helpers/tableAndToolbar/actions';
import PlaylistAddCheckOutlinedIcon from '@material-ui/icons/PlaylistAddCheckOutlined';

const useStyles = makeStyles(theme => ({
  button: {
    margin: '0px 8px',
    fontWeight: 'normal',
    color: 'rgba(0,0,0,.55) !important',
    opacity: '1 !important'
  },
  selected: {
    color: theme.palette.primary.main,
    margin: '0px 8px'
  },
  checkbox: {
    paddingLeft: '0px'
  },
  list: {
    overflow: 'auto',
    height: '560px',
    maxHeight: '40vh'
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '560px',
    maxHeight: '40vh'
  },
  paginatedFilterPanel: {
    padding: '8px',
    width: '300px'
  },
  label: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width: '200px'
  },
  searchBar: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  filterButton: {
    margin: '0px 8px',
    fontWeight: 'normal',
    color: 'rgba(0,0,0,.55) !important',
    opacity: '1 !important',
    '&:hover': {
      color: theme.palette.primary.main + '!important'
    }
  },
  selected: {
    color: theme.palette.primary.main,
    margin: '0px 8px'
  },
  noResultsDiv: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    fontSize: '16px',
    height: '560px',
    maxHeight: '40vh'
  },
  clearPaginatedFilter: {
    display: 'flex',
    justifyContent: 'center',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start'
  },
  radioLabel: {
    margin: '0 4px',
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
      color: 'rgba(0,0,0,.65)'
    },
    '& .MuiRadio-root': {
      paddingLeft: '4px',
      paddingRight: '4px'
    }
  }
}));


export default function PaginatedFilters(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { filter, action } = props;
  const { selectedOptions, options, onChange, title, searchPlaceholder, filterAction, isLoading, filterType } = filter;

  const pageNo = useSelector(store => store.tableAndToolbar.paginatedFilters.pageNo);
  const rowsPerPage = useSelector(store => store.tableAndToolbar.paginatedFilters.rowsPerPage);
  const totalCount = useSelector(store => store.tableAndToolbar.paginatedFilters.totalCount);
  const filterProductNames = useSelector(store => store.tableAndToolbar.filterProductNames);

  const [isSelected, setIsSelected] = useState(false);
  const [open, setOpen] = useState(false);
  const [isViewSelected, setIsViewSelected] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const search = useDebounce(searchValue, 500);
  const anchorRef = React.useRef(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    setIsSelected(selectedOptions.length > 0);
  }, [selectedOptions]);

  useEffect(() => {
    if(open) {
      dispatch(updatePaginatedFilters({
        pageNo: 0,
        searchInput: '',
        rowsPerPage: 10,
        totalCount: 0
      }));
      resetSearch();
      if (filterAction) {
        dispatch(filterAction);
      }
    }
  }, [open]);

  useEffect(() => {
    if(searchPlaceholder) {
      if (search.length > 2 || search === '') {
        dispatch(updatePaginatedFilters({ searchInput: search.trim(), pageNo: 0, totalCount: 0 }));
        if (filterAction && open) {
          dispatch(filterAction);
        }
      }
    }
  }, [search]);

  const onSearchChange = event => {
    let value = event.target.value;
    setSearchValue(value);
  };

  const resetSearch = () => {
    setSearchValue('');
  };

  const handleStatusChange = (event) => {
    const selectedValue = event.target.value;
    setStatusFilter(selectedValue);
    
    const filterMapping = {
      all: null,
      active: true,
      archived: false
    };
    
    const filterUpdate = { pageNo: 0, totalCount: 0 };
    if (filterType === 'campaign') {
      filterUpdate.activeCampaignFilter = filterMapping[selectedValue];
    } else {
      filterUpdate.activeTemplateFilter = filterMapping[selectedValue];
    }
    
    dispatch(updatePaginatedFilters(filterUpdate));
    
    if (filterAction) {
      dispatch(filterAction);
    }
  };

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
    setIsViewSelected(false);
  };

  const handleChange = event => {
    let newOptions = (isViewSelected ? selectedOptions : options).map(option => {
      if (option.value === event.value) {
        return { ...option, checked: !option.checked };
      }
      return option;
    });
    onChange(
      newOptions
    );
    dispatch(action);
  };

  const handlePageChange = (event, page) => {
    dispatch(updatePaginatedFilters({ pageNo: page}));
    if(filterAction) {
      dispatch(filterAction);
    }
  };

  const changeView = () => {
    setIsViewSelected(view => !view);
  };
  
  const resetSelectedFilters = () => {
    let newOptions = selectedOptions.map(option => {
      return { ...option, checked: false };
    });
    onChange(
      newOptions
    );
    dispatch(action);
  };

  return (
    <>
      <Button
        aria-label={`Filter ${title}`}
        ref={anchorRef}
        className={isSelected ? classes.selected : classes.button}
        onClick={handleToggle}
        endIcon={<ExpandMoreIcon />}>
        {title}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        popperOptions={{ positionFixed: true }}>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom'
            }}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <div className={classes.paginatedFilterPanel}>
                  <div className={classes.searchBar}>
                    {isViewSelected ? (
                      <div className={classes.clearPaginatedFilter}>
                        <Button disabled={selectedOptions.length === 0} className={classes.filterButton} onClick={resetSelectedFilters}>Clear Selection</Button>
                      </div>
                      ) : (searchPlaceholder && <SearchInput
                        className={classes.searchInput}
                        placeholder={searchPlaceholder}
                        value={searchValue}
                        onChange={onSearchChange}
                        onClear={resetSearch}
                      />)}
                    <div>
                      <Tooltip title={`${isViewSelected ? `Hide` : `View`} Selected Options`}>
                        <IconButton
                          variant="outlined"
                          className={isViewSelected ? classes.selected : classes.filterButton}
                          onClick={changeView}>
                            <PlaylistAddCheckOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>

                  {!isViewSelected && (
                    <FormControl component="fieldset">
                      <RadioGroup
                        className={classes.radioGroup}
                        value={statusFilter}
                        onChange={handleStatusChange}
                        row
                      >
                        <FormControlLabel
                          value="all"
                          control={<Radio color="primary" size="small" />}
                          label="All"
                          className={classes.radioLabel}
                        />
                        <FormControlLabel
                          value="active"
                          control={<Radio color="primary" size="small" />}
                          label="Active"
                          className={classes.radioLabel}
                        />
                        <FormControlLabel
                          value="archived"
                          control={<Radio color="primary" size="small" />}
                          label="Archived"
                          className={classes.radioLabel}
                        />
                      </RadioGroup>
                    </FormControl>
                  )}
                  
                  {isViewSelected ? (
                    selectedOptions.length === 0 ? (
                      <div className={classes.list}>
                        <Typography className={classes.noResultsDiv}>
                          No filters selected.
                        </Typography>
                      </div>
                    ) : (
                      <div>
                        <MenuList className={classes.list} id="split-button-menu2">
                          {selectedOptions.map((option, index) => (
                            <Tooltip
                              title={option.label.toUpperCase()}>
                              <MenuItem key={index} onClick={() => handleChange(option)}>
                                <Checkbox
                                  checked={option.checked}
                                  color="primary"
                                  className={classes.checkbox}
                                  value="true"
                                />
                                <Typography variant="inherit" className={classes.label}>
                                  {option.label.toUpperCase()}
                                </Typography>
                              </MenuItem>
                            </Tooltip>
                          ))}
                        </MenuList>
                      </div>
                    )
                  ) : (
                    <div>
                      {!isLoading && totalCount === 0 && (
                        <React.Fragment>
                          <Typography className={classes.noResultsDiv}>
                            No records found.
                          </Typography>
                        </React.Fragment>
                      )}
                      {isLoading ? (
                        <div className={classes.loader}>
                          <CircularProgress />
                        </div>
                      ) : (totalCount !== 0 && (
                        <MenuList className={classes.list} id="split-button-menu2">
                          {options.map((option, index) => (
                            <Tooltip
                              title={option.label.toUpperCase()}>
                              <MenuItem key={index} onClick={() => handleChange(option)}>
                                <Checkbox
                                  checked={option.checked}
                                  color="primary"
                                  className={classes.checkbox}
                                  value="true"
                                />
                                <Typography variant="inherit" className={classes.label}>
                                  {option.label.toUpperCase()}
                                </Typography>
                              </MenuItem>
                            </Tooltip>
                          ))}
                        </MenuList>
                      )
                      )}
                      <TablePagination
                        component="div"
                        count={totalCount}
                        onPageChange={handlePageChange}
                        page={pageNo}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={[]}
                      />
                    </div>
                  )}
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
